import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import PageHeader from '../../components/UI/PageHeader';
import Button from '../../components/UI/Button';
import { createCompletion, getCompletionsHistory } from '../../services/completions/completionsService';
import {
  MessageType,
  UserChatMessageDto
} from '../../services/completions/types';
import ReactMarkdown from 'react-markdown';

interface ChatMessage {
  id: string;
  content: string;
  type: MessageType;
  timestamp: Date;
  isLoading?: boolean;
  correlation_id?: string;
}

const ChatInterface: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFetchingHistory, setIsFetchingHistory] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [pollingActive, setPollingActive] = useState(false);
  const [lastMessageId, setLastMessageId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const pageSize = 20;
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const auth = useAuth();

  // Fetch chat history with pagination
  const fetchChatHistory = useCallback(async (page: number = 1, scrollToEnd = true) => {
    if (!auth.isAuthenticated) {
      return;
    }

    // Set appropriate loading state
    if (page === 1) {
      setIsFetchingHistory(true);
    } else {
      setIsLoadingMore(true);
    }
    setError(null);

    try {
      const historyData = await getCompletionsHistory(auth.user || null, page, pageSize);

      // Map API responses to chat message format using the UserChatMessageDto structure
      const newMessages: ChatMessage[] = historyData.content.map((item: UserChatMessageDto) => ({
        id: item.id || `temp-${Date.now()}`,
        content: typeof item.content === 'string' ? item.content : '',
        type: item.type || MessageType.SYSTEM,
        timestamp: item.created_at ? new Date(item.created_at) : new Date(),
        correlation_id: item.correlation_id
      })).reverse(); // Show oldest messages first

      if (page === 1) {
        // Initial load
        setMessages(newMessages);
        
        // Track latest message ID for polling
        if (newMessages.length > 0) {
          const latestMessage = [...newMessages].sort((a, b) =>
            b.timestamp.getTime() - a.timestamp.getTime()
          )[0];
          setLastMessageId(latestMessage.id);
        }

        // Add welcome message if no history
        if (newMessages.length === 0) {
          setMessages([{
            id: `system-${Date.now()}`,
            content: 'Hello! How can I help you today?',
            type: MessageType.SYSTEM,
            timestamp: new Date(),
          }]);
        }

        // Scroll to bottom if requested
        if (scrollToEnd) {
          setTimeout(scrollToBottom, 100);
        }
      } else {
        // Load more - add older messages at the beginning
        setMessages(prev => [...newMessages, ...prev]);

        // Restore scroll position after loading more messages
        setTimeout(() => {
          const savedPosition = (window as any).__scrollPosition;
          if (savedPosition && messagesContainerRef.current) {
            const newScrollHeight = messagesContainerRef.current.scrollHeight;
            const heightDifference = newScrollHeight - savedPosition.height;
            messagesContainerRef.current.scrollTop = savedPosition.top + heightDifference;
          }
          delete (window as any).__scrollPosition;
        }, 100);
      }

      // Check if there are more messages
      setHasMore(
        historyData.content.length === pageSize &&
        page * pageSize < (historyData.total_elements || 0)
      );
    } catch (err) {
      console.error('Error loading chat history:', err);
      setError('Failed to load chat history.');

      // Add welcome message on initial load error
      if (page === 1 && messages.length === 0) {
        setMessages([{
          id: `system-${Date.now()}`,
          content: 'Hello! How can I help you today?',
          type: MessageType.SYSTEM,
          timestamp: new Date(),
        }]);
      }
    } finally {
      setIsFetchingHistory(false);
      setIsLoadingMore(false);
    }
  }, [auth.isAuthenticated, auth.user, pageSize]);

  // Fetch only new messages since the last known message
  const fetchNewMessages = useCallback(async () => {
    if (!auth.isAuthenticated || isLoading || isFetchingHistory) {
      return;
    }

    try {
      const historyData = await getCompletionsHistory(auth.user || null, 1, 10);

      if (historyData.content.length === 0) return;

      // Find messages newer than our last known message
      let newMessages: ChatMessage[] = [];

      // Sort by created_at (newest first)
      const sortedHistoryData = [...historyData.content].sort((a, b) => {
        const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
        const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
        return dateB - dateA;
      });

      // Process only if we have new messages
      if (lastMessageId) {
        // Find messages that are newer than our last message
        // or that aren't already in our message list
        const existingIds = messages.map(m => m.id);

        for (const item of sortedHistoryData) {
          const messageId = item.id || '';

          // If we already have this message, skip it
          if (existingIds.includes(messageId)) {
            continue;
          }

          // Otherwise, it's a new message
          newMessages.push({
            id: messageId,
            content: typeof item.content === 'string' ? item.content : '',
            type: item.type || MessageType.SYSTEM,
            timestamp: item.created_at ? new Date(item.created_at) : new Date(),
            correlation_id: item.correlation_id
          });
        }
      } else {
        // If we don't have a last message ID, just take the newest message
        const newestMessage = sortedHistoryData[0];
        if (newestMessage) {
          newMessages.push({
            id: newestMessage.id || `temp-${Date.now()}`,
            content: typeof newestMessage.content === 'string' ? newestMessage.content : '',
            type: newestMessage.type || MessageType.SYSTEM,
            timestamp: newestMessage.created_at ? new Date(newestMessage.created_at) : new Date(),
            correlation_id: newestMessage.correlation_id
          });
        }
      }

      if (newMessages.length > 0) {
        // Update the last message ID
        setLastMessageId(newMessages[0].id);

        // Add new messages and sort chronologically
        setMessages(currentMessages => {
          // Combine current and new messages
          const updatedMessages = [...currentMessages];

          // Remove any "loading" messages as they've been replaced by real messages
          const filteredMessages = updatedMessages.filter(msg => !msg.isLoading);

          // Add only messages we don't already have
          const existingIds = filteredMessages.map(m => m.id);
          for (const msg of newMessages) {
            if (!existingIds.includes(msg.id)) {
              filteredMessages.push(msg);
            }
          }

          // Sort by timestamp (oldest first)
          return filteredMessages.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
        });

        // Scroll to bottom to show new messages
        setTimeout(scrollToBottom, 100);
      }
    } catch (err) {
      console.error('Error fetching new messages:', err);
      // Don't show error to user for background polling
    }
  }, [auth.isAuthenticated, auth.user, isLoading, isFetchingHistory, lastMessageId, messages]);

  // Start polling for new messages
  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    setPollingActive(true);
    pollingIntervalRef.current = setInterval(() => {
      fetchNewMessages();
    }, 5000); // Poll every 5 seconds
  }, [fetchNewMessages]);

  // Stop polling for new messages
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setPollingActive(false);
  }, []);

  // Start polling for new messages when component mounts
  useEffect(() => {
    // Load initial history
    fetchChatHistory(1, true);

    // Start polling
    startPolling();

    // Cleanup on unmount
    return () => {
      stopPolling();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-resize textarea based on content
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [inputMessage]);

  // Scroll to most recent messages when messages change
  useEffect(() => {
    if (!isFetchingHistory) {
      scrollToBottom();
    }
  }, [messages, isFetchingHistory]);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputMessage(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Send message with Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
    
    // Add focus to textarea when Escape is pressed
    if (e.key === 'Escape') {
      e.preventDefault();
      textareaRef.current?.blur();
      setTimeout(() => textareaRef.current?.focus(), 10);
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || !auth.isAuthenticated) return;

    const correlationId = `user-${Date.now()}`;
    const userMessage: ChatMessage = {
      id: correlationId,
      content: inputMessage,
      type: MessageType.USER,
      timestamp: new Date(),
      correlation_id: correlationId
    };

    // Add temporary loading message for assistant
    const loadingMessage: ChatMessage = {
      id: `loading-${Date.now()}`,
      content: '',
      type: MessageType.ASSISTANT,
      timestamp: new Date(),
      isLoading: true,
      correlation_id: correlationId
    };

    // Update UI with new messages
    setMessages(prev => [...prev, userMessage, loadingMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await createCompletion(
        auth.user || null,
        {
          message: userMessage.content,
          correlation_id: correlationId,
        });

      // Process response and update messages
      setMessages(prev => {
        // Remove loading message
        const filtered = prev.filter(msg => !msg.isLoading);

        // Handle the case where the response may already have been added by polling
        const responseExists = filtered.some(msg =>
          response.length > 0 && msg.id === response[0].id
        );

        if (responseExists) {
          return filtered;
        }

        // Create assistant message from response if not already present
        if (response.length > 0) {
          const assistantResponse = response[0];
          const newMessage: ChatMessage = {
            id: assistantResponse.id || `assistant-${Date.now()}`,
            content: assistantResponse.message?.content || 'Desculpe, ocorreu um erro ao processar sua solicitação.',
            type: assistantResponse.message_type || MessageType.ASSISTANT,
            timestamp: assistantResponse.created_at ? new Date(assistantResponse.created_at) : new Date(),
            correlation_id: assistantResponse.correlation_id || correlationId
          };

          // Set latest message ID
          setLastMessageId(newMessage.id);

          return [...filtered, newMessage];
        }

        return filtered;
      });

      // Ensure we refresh history to catch any messages we might have missed
      fetchChatHistory(1, true);
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Failed to send message. Please try again.');

      // Remove loading message in case of error
      setMessages(prev => prev.filter(msg => !msg.isLoading));
    } finally {
      setIsLoading(false);
    }
  };

  // Unused function - commented out
  /*
  const handleClearChat = () => {
    // Confirm before clearing
    if (window.confirm('Are you sure you want to clear the chat history?')) {
      setMessages([{
        id: `system-${Date.now()}`,
        content: 'History cleared. How can I help you today?',
        type: MessageType.SYSTEM,
        timestamp: new Date(),
      }]);
      setLastMessageId(null);
    }
  };
  */

  const handleLoadMore = () => {
    if (!isLoadingMore && hasMore) {
      // Save current scroll position
      if (messagesContainerRef.current) {
        const scrollHeight = messagesContainerRef.current.scrollHeight;
        const scrollTop = messagesContainerRef.current.scrollTop;

        (window as any).__scrollPosition = {
          height: scrollHeight,
          top: scrollTop,
          messageCount: messages.length
        };
      }

      // Load next page
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchChatHistory(nextPage, false);
    }
  };

  const handleRefreshChat = useCallback(() => {
    setCurrentPage(1);
    fetchChatHistory(1, true);
  }, [fetchChatHistory]);

  // Format message content with multi-line support
  const formatMessage = useCallback((content: string) => {
    // Using components to apply styles to ReactMarkdown
    return (
      <ReactMarkdown
        components={{
          // Apply styles to paragraphs
          p: ({node, ...props}) => <p className="whitespace-pre-wrap break-words" {...props} />
        }}
      >
        {content}
      </ReactMarkdown>
    );
  }, []);

  // Group messages by date for better visual organization
  const groupMessagesByDate = () => {
    const groups: { date: string; messages: ChatMessage[] }[] = [];
    
    messages.forEach(message => {
      const date = message.timestamp;
      const dateString = date.toDateString();
      
      const existingGroup = groups.find(g => g.date === dateString);
      if (existingGroup) {
        existingGroup.messages.push(message);
      } else {
        groups.push({
          date: dateString,
          messages: [message]
        });
      }
    });
    
    return groups;
  };

  const messageGroups = groupMessagesByDate();

  return (
    <div className="flex flex-col h-[calc(100vh-120px)]">
      <PageHeader
        title="Synapse Chat"
        breadcrumbs={[
          { label: "Dashboard", to: "/" },
          { label: "Chat" }
        ]}
      />

      {/* Main chat container */}
      <div className="flex-grow flex flex-col bg-gray-50 rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        {/* Toolbar */}
        <div className="bg-white p-3 border-b border-gray-200 flex justify-between items-center">
          <div className="flex items-center">
            <span className="font-medium">Chat Assistant</span>
            <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
              pollingActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
            }`}>
              {pollingActive ? 'Online' : 'Offline'}
            </span>
          </div>

          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefreshChat}
              title="Refresh conversation"
              disabled={isFetchingHistory}
            >
              <i className={`fas fa-sync-alt mr-1 ${isFetchingHistory ? 'animate-spin' : ''}`}></i>
              Refresh
            </Button>
          </div>
        </div>

        {/* Message area with scroll */}
        <div
          ref={messagesContainerRef}
          className="flex-grow overflow-y-auto p-4 space-y-4"
        >
          {isFetchingHistory && messages.length === 0 ? (
            <div className="flex justify-center items-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <i className="fas fa-comment-dots text-4xl mb-3"></i>
              <p>No messages yet. Start a conversation!</p>
            </div>
          ) : (
            <>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg mx-auto max-w-lg">
                  {error}
                  <Button
                    variant="ghost"
                    size="xs"
                    className="ml-2"
                    onClick={() => setError(null)}
                  >
                    Close
                  </Button>
                </div>
              )}

              {/* Load more button */}
              {hasMore && (
                <div className="flex justify-center py-3">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleLoadMore}
                    disabled={isLoadingMore}
                  >
                    {isLoadingMore ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Loading...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-chevron-up mr-2"></i>
                        Load More
                      </>
                    )}
                  </Button>
                </div>
              )}

              {/* Beginning of conversation marker */}
              {!hasMore && (
                <div className="text-center py-3 mb-2">
                  <span className="bg-gray-200 text-gray-600 px-3 py-1 rounded-full text-xs font-medium">
                    Beginning of conversation
                  </span>
                </div>
              )}

              {/* Messages grouped by date */}
              {messageGroups.map((group, groupIndex) => (
                <div key={groupIndex} className="mb-6">
                  {/* Date separator */}
                  <div className="flex justify-center mb-4">
                    <div className="bg-gray-200 text-gray-600 px-3 py-1 rounded-full text-xs font-medium">
                      {new Date(group.date).toLocaleDateString([], { 
                        weekday: 'long',
                        month: 'long', 
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </div>
                  </div>

                  {/* Messages for this date */}
                  {group.messages.map((message) => (
                    <div
                      key={message.id}
                      className={`flex ${message.type === MessageType.USER ? 'justify-end' : 'justify-start'} mb-4`}
                    >
                      <div
                        className={`max-w-3xl rounded-lg p-4 ${
                          message.isLoading ? 'bg-gray-100 animate-pulse' :
                          message.type === MessageType.USER ? 'bg-blue-100 text-blue-900' :
                          message.type === MessageType.SYSTEM ? 'bg-gray-100 text-gray-800 border border-gray-200' :
                          'bg-green-50 text-gray-800 border border-green-100'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-1">
                          <div className="font-medium text-sm">
                            {message.type === MessageType.USER ? 'User' :
                             message.type === MessageType.SYSTEM ? 'System' : 'Assistant'}
                          </div>
                          {!message.isLoading && (
                            <div className="text-xs text-gray-500 ml-4">
                              {message.timestamp.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                            </div>
                          )}
                        </div>

                        <div className="prose prose-sm max-w-none">
                          {message.isLoading ? (
                            <div className="flex items-center space-x-2">
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
                            </div>
                          ) : (
                            <div className="whitespace-pre-wrap break-words">
                              {formatMessage(message.content)}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ))}

              {/* Invisible element to scroll to end */}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input area */}
        <div className="bg-white p-3 sm:p-4 border-t border-gray-200">
          <div className="flex items-end space-x-2 max-w-4xl mx-auto">
            <div className="flex-grow relative">
              <div className="relative rounded-lg shadow-sm bg-gray-50 hover:bg-white transition-all duration-200 border border-gray-300 focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 focus-within:bg-white focus-within:shadow-md">
                <textarea
                  ref={textareaRef}
                  value={inputMessage}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  onFocus={() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })}
                  placeholder="Type your message here..."
                  className="w-full pl-3 sm:pl-4 pr-10 sm:pr-12 py-2 sm:py-3 bg-transparent focus:outline-none resize-none min-h-[52px] max-h-[200px] rounded-lg text-sm sm:text-base transition-shadow duration-200"
                  rows={1}
                  disabled={isLoading}
                  autoComplete="off"
                  spellCheck="true"
                />
                <div className="absolute right-2 sm:right-3 bottom-2 sm:bottom-3 flex items-center">
                  {isLoading ? (
                    <div className="text-blue-500 mr-1">
                      <div className="animate-pulse flex space-x-1">
                        <div className="h-1.5 sm:h-2 w-1.5 sm:w-2 bg-blue-400 rounded-full"></div>
                        <div className="h-1.5 sm:h-2 w-1.5 sm:w-2 bg-blue-400 rounded-full"></div>
                        <div className="h-1.5 sm:h-2 w-1.5 sm:w-2 bg-blue-400 rounded-full"></div>
                      </div>
                    </div>
                  ) : (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={isLoading || !inputMessage.trim()}
                      onClick={handleSendMessage}
                      className={`rounded-full p-1.5 sm:p-2 ${inputMessage.trim() ? 'text-blue-500 hover:text-white hover:bg-blue-500' : 'text-gray-400'}`}
                      title="Enviar mensagem"
                    >
                      <i className="fas fa-paper-plane text-sm sm:text-base"></i>
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-between items-center text-xs text-gray-500 mt-2 px-1">
            <div className="hidden sm:block">
              <i className="fas fa-keyboard mr-1"></i>
              Shift+Enter for new line
            </div>
            <div className="sm:hidden">
              <i className="fas fa-keyboard mr-1"></i>
              Shift+Enter
            </div>
            <div>
              <i className="fas fa-robot mr-1"></i>
              AI Assistant
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;